import {Presenter} from '../../../Support/with_presenter';
import {PartialWorkout} from '../../../Models/PartialWorkout';
import {Distance} from '../../../Domain/Distance';
import {Duration} from '../../../Domain/Duration';
import {Speed} from '../../../Domain/Speed';
import {DateRange} from '../../../Domain/DateRange';
import {WorkoutListViewModel} from '../../Components/Workout/WorkoutList';
import {StatisticsProvider} from '../../../Business/StatisticsProvider';
import {WorkoutsService} from '../../../Network/WorkoutsService';
import {workoutTitleFormatter} from '../../Support/workoutTitleFormatter';
import {WorkoutsTableFilter} from './WorkoutsTableFilter';
import { calculateIndoorWorkoutListSummary } from '../../../Strokes/calculateIndoorWorkoutListSummary';

export class WorkoutsTablePresenter implements Presenter {
    public filter: WorkoutsTableFilter;

    public selectedWorkoutIds: number[] = [];

    private deletedWorkoutIds: number[] = [];

    private pageSize: number = 25;

    public get pages(): number[] {
        const numPages = this.numPages;
        if (numPages <= 5) {
            return Array.from(Array(numPages).keys()).map((i) => i + 1);
        }
        const currentPage = this.filter.page;
        const pages = [];
        if (currentPage <= 3) {
            pages.push(1, 2, 3, 4, 5);
        }
        if (currentPage > 3 && currentPage < numPages - 2) {
            pages.push(currentPage - 2, currentPage - 1, currentPage, currentPage + 1, currentPage + 2);
        }
        if (currentPage >= numPages - 2) {
            pages.push(numPages - 4, numPages - 3, numPages - 2, numPages - 1, numPages);
        }

        return pages.filter((page) => page >= 1 && page <= numPages);
    }

    public get numPages(): number {
        return Math.ceil(this.filteredPartialWorkouts.length / this.pageSize);
    }

    private _partialWorkouts: PartialWorkout[] = [];

    public get filteredPartialWorkouts(): PartialWorkout[] {
        return this._partialWorkouts.filter((partialWorkout) => {
            if (this.deletedWorkoutIds.includes(partialWorkout.id)) {
                return false;
            }

            // Get workout metrics

            const formattedTitle = workoutTitleFormatter(partialWorkout);

            const indoorWorkoutListSummary = calculateIndoorWorkoutListSummary(partialWorkout);
            const distance = indoorWorkoutListSummary.distance;
            const duration = indoorWorkoutListSummary.duration;

            // Duration filter
            if (this.filter.durationRange && this.filter.durationRange.min) {
                if (this.filter.durationRange.min.gt(duration)) {
                    return false;
                }
            }

            if (this.filter.durationRange && this.filter.durationRange.max) {
                if (this.filter.durationRange.max.lt(duration)) {
                    return false;
                }
            }

            // Distance filter
            if (this.filter.distanceRange && this.filter.distanceRange.min) {
                if (this.filter.distanceRange.min.gt(distance)) {
                    return false;
                }
            }

            if (this.filter.distanceRange && this.filter.distanceRange.max) {
                if (this.filter.distanceRange.max.lt(distance)) {
                    return false;
                }
            }

            // Name filter with null title handling
            if (this.filter.title && this.filter.title.trim()) {
                // If title is not null and matches the filter, include the workout
                if (!(formattedTitle && formattedTitle.toLowerCase().includes(this.filter.title.toLowerCase()))) {
                    return false;
                }
            }

            if (this.filter === null || !this.filter.dateRange) {
                return true;
            } else {
                const {from, till} = this.filter.dateRange;
                if (from !== null && partialWorkout.executed_at < from) {
                    return false;
                }
                if (till !== null && partialWorkout.executed_at > till) {
                    return false;
                }
            }

            return true;
        });
    }

    public get numWorkouts(): number {
        return this.filteredPartialWorkouts.length;
    }

    public get workoutListViewModels(): WorkoutListViewModel[] {
        const partialWorkouts = this.filteredPartialWorkouts;
        const start = (this.filter.page - 1) * this.pageSize;
        const end = start + this.pageSize;

        const partialWorkoutsOnPage = partialWorkouts.slice(start, end);

        return partialWorkoutsOnPage.map((partialWorkout) => {
            return WorkoutListViewModel.fromPartialWorkout(partialWorkout);
        });
    }

    public get totalDistance(): Distance {
        return StatisticsProvider.create(this.filteredPartialWorkouts).totalDistance;
    }

    public get averageSpeed(): Speed {
        return StatisticsProvider.create(this.filteredPartialWorkouts).averageSpeed;
    }

    public constructor(
        _initialPartialFilter: Partial<WorkoutsTableFilter> | null | undefined,
        _partialWorkouts: PartialWorkout[],
        private workoutsService: WorkoutsService,
    ) {
        this.filter = this.filterFromPartial(_initialPartialFilter) ?? {page: 1};
        this._partialWorkouts = _partialWorkouts;
    }

    public mount(): void {
        /* Noop */
    }

    public unmount(): void {
        /* Noop */
    }

    public resetFilter() {
        this.filter = {
            page: 1,
        };
    }

    public onRangeChange(dateRange: DateRange | null): void {
        this.filter = {
            ...this.filter,
            page: this.filter.page,
            dateRange: dateRange ? dateRange : undefined,
        };
    }

    public onTitleFilterChange(title: string | ''): void {
        this.filter = {
            ...this.filter,
            page: this.filter.page,
            title: title ? title : undefined,
        };
    }

    public onMinDistanceChange(km: number | string): void {
        let min;
        if (km === '') {
            min = null;
        } else {
            min = Number(km) < 0 ? null : Distance.fromKilometers(Number(km));
        }
        this.filter = {
            ...this.filter,
            distanceRange: {
                min: min,
                max: this.filter.distanceRange?.max ?? null,
            },
        };
    }

    public onMaxDistanceChange(km: number | string): void {
        let max;
        if (km === '') {
            max = null;
        } else {
            max = Number(km) < 0 ? null : Distance.fromKilometers(Number(km));
        }
        this.filter = {
            ...this.filter,
            distanceRange: {
                min: this.filter.distanceRange?.min ?? null,
                max: max,
            },
        };
    }

    public onMinDurationChange(minutes: number | string): void {
        let min;
        if (minutes === '') {
            min = null;
        } else {
            min = Number(minutes) < 0 ? null : Duration.fromMinutes(Number(minutes));
        }
        this.filter = {
            ...this.filter,
            durationRange: {
                min: min,
                max: this.filter.durationRange?.max ?? null,
            },
        };
    }

    public onMaxDurationChange(minutes: number | string): void {
        let max;
        if (minutes === '') {
            max = null;
        } else {
            max = Number(minutes) < 0 ? null : Duration.fromMinutes(Number(minutes));
        }
        this.filter = {
            ...this.filter,
            durationRange: {
                min: this.filter.durationRange?.min ?? null,
                max: max,
            },
        };
    }

    public onPageChange(page: number): void {
        if (page < 1 || page > this.numPages) {
            return;
        }
        this.filter = {
            ...this.filter,
            page: page,
            dateRange: undefined,
        };
    }

    public nextPage(): void {
        this.filter = {
            ...this.filter,
            page: Math.min(this.filter.page + 1, this.numPages),
        };
    }

    public previousPage(): void {
        this.filter = {
            ...this.filter,
            page: Math.max(this.filter.page - 1, 1),
        };
    }

    public onPartialFilterChange(partialFilter: Partial<WorkoutsTableFilter> | null | undefined): void {
        const filter = this.filterFromPartial(partialFilter);
        if (filter) {
            this.filter = filter;
        }
    }

    private filterFromPartial(
        partialFilter: Partial<WorkoutsTableFilter> | null | undefined,
    ): WorkoutsTableFilter | null {
        if (!partialFilter) {
            return null;
        }

        const dateRange = !partialFilter.dateRange
            ? null
            : {
                  from: partialFilter.dateRange.from ?? null,
                  till: partialFilter.dateRange.till ?? null,
              };

        //Make sure the "till" is always at the end of the day
        if (dateRange && dateRange.till) {
            dateRange.till.setHours(23, 59, 59, 999);
        }

        return {
            dateRange: dateRange ? dateRange : undefined,
            page: partialFilter.page ? parseInt(partialFilter.page as any, 10) : 1,
            title: partialFilter.title || undefined,
            distanceRange: partialFilter.distanceRange || undefined,
            durationRange: partialFilter.durationRange || undefined,
        };
    }

    public onSelectionChanged(workoutIds: number[]): void {
        this.selectedWorkoutIds = workoutIds;
    }

    public async deleteSelectedWorkouts() {
        this.filter = {
            ...this.filter,
            dateRange: undefined,
        };
        const selectedWorkoutIds = this.selectedWorkoutIds;
        try {
            this.selectedWorkoutIds = [];
            this.deletedWorkoutIds = [...this.deletedWorkoutIds, ...selectedWorkoutIds];
            await this.workoutsService.deleteAll(selectedWorkoutIds);
        } catch (e) {
            this.deletedWorkoutIds = [...this.deletedWorkoutIds.filter((id) => !selectedWorkoutIds.includes(id))];
            throw e;
        }
    }

    public clearSelectedWorkouts() {
        this.selectedWorkoutIds = [];
    }
}
